/**
 * Search Service - Full-text Search
 * 
 * Provides full-text search across all content types using PostgreSQL tsvector
 */

import { safeFrom, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError
} from '@/lib/service-errors';
import { safeAuth } from '@/lib/safe-supabase';
import type { 
  SearchResult, 
  SearchOptions, 
  SearchContentType,
  SearchSuggestion,
  SavedSearch,
  SearchAnalytics
} from '@/types/search';

/**
 * Search across all content types
 */
export const searchAll = async (
  query: string,
  options: SearchOptions = {}
): Promise<ServiceResponse<SearchResult[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  if (!query || query.trim().length === 0) {
    return successResponse([]);
  }

  try {
    const searchTerm = query.trim();
    const contentType = options.contentType || 'all';
    const limit = options.limit || 20;
    const offset = options.offset || 0;

    const results: SearchResult[] = [];

    // Search pages
    if (contentType === 'all' || contentType === 'page') {
      try {
        // Try full-text search first (only if search_vector exists)
        let pages: any[] = [];
        let useFallback = false;
        
        try {
          const result = await safeFrom('pages')
            .select('id, title, description, slug, search_vector')
            .eq('is_published', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('id', { ascending: false });
          
          if (result.error) {
            useFallback = true;
          } else {
            pages = result.data || [];
            // If no results from textSearch, try fallback
            if (pages.length === 0) {
              useFallback = true;
            }
          }
        } catch (textSearchError) {
          // If textSearch fails, use fallback
          useFallback = true;
          console.warn('[searchService] textSearch failed for pages, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if needed
        if (useFallback && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('pages')
            .select('id, title, description, slug')
            .eq('is_published', true)
            .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('id', { ascending: false });
          
          if (!fallbackResult.error && fallbackResult.data) {
            pages = fallbackResult.data;
          }
        }

        if (pages && pages.length > 0) {
          pages.forEach((page: any) => {
            results.push({
              id: page.id,
              type: 'page',
              title: page.title || 'Untitled Page',
              excerpt: page.description || '',
              link: `/${page.slug}`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching pages:', error);
      }
    }

    // Search news
    if (contentType === 'all' || contentType === 'news') {
      try {
        // Try full-text search first
        let news: any[] = [];
        let newsError: any = null;
        
        try {
          const result = await safeFrom('news')
            .select('id, title, excerpt, search_vector')
            .eq('published', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('created_at', { ascending: false });
          
          news = result.data || [];
          newsError = result.error;
        } catch (textSearchError) {
          console.warn('[searchService] textSearch failed for news, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if textSearch returned no results or failed
        if ((newsError || news.length === 0) && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('news')
            .select('id, title, excerpt')
            .eq('published', true)
            .or(`title.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('created_at', { ascending: false });
          
          if (!fallbackResult.error && fallbackResult.data) {
            news = fallbackResult.data;
          }
        }

        if (news && news.length > 0) {
          news.forEach((article: any) => {
            results.push({
              id: article.id,
              type: 'news',
              title: article.title || 'Untitled Article',
              excerpt: article.excerpt || '',
              link: `/news#article-${article.id}`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching news:', error);
      }
    }

    // Search projects
    if (contentType === 'all' || contentType === 'project') {
      try {
        const { data: projects, error: projectsError } = await safeFrom('projects')
          .select('id, title, description, category')
          .eq('is_published', true)
          .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,category.ilike.%${searchTerm}%`)
          .limit(limit)
          .order('sort_order', { ascending: false })
          .order('created_at', { ascending: false });

        if (!projectsError && projects) {
          projects.forEach((project: any) => {
            results.push({
              id: project.id,
              type: 'project',
              title: project.title || 'Untitled Project',
              excerpt: project.description || '',
              link: `/projects#project-${project.id}`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching projects:', error);
      }
    }

    // Search events
    if (contentType === 'all' || contentType === 'event') {
      try {
        // Try full-text search first
        let events: any[] = [];
        let eventsError: any = null;
        
        try {
          const result = await safeFrom('events')
            .select('id, title, description, search_vector')
            .eq('published', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('date', { ascending: true });
          
          events = result.data || [];
          eventsError = result.error;
        } catch (textSearchError) {
          console.warn('[searchService] textSearch failed for events, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if textSearch returned no results or failed
        if ((eventsError || events.length === 0) && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('events')
            .select('id, title, description')
            .eq('published', true)
            .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('date', { ascending: true });
          
          if (!fallbackResult.error && fallbackResult.data) {
            events = fallbackResult.data;
          }
        }

        if (events && events.length > 0) {
          events.forEach((event: any) => {
            results.push({
              id: event.id,
              type: 'event',
              title: event.title || 'Untitled Event',
              excerpt: event.description || '',
              link: `/events#event-${event.id}`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching events:', error);
      }
    }

    // Search photo albums
    if (contentType === 'all' || contentType === 'gallery') {
      try {
        // Try full-text search first
        let albums: any[] = [];
        let albumsError: any = null;
        
        try {
          const result = await safeFrom('photo_albums')
            .select('id, title, description, search_vector')
            .eq('is_published', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('created_at', { ascending: false });
          
          albums = result.data || [];
          albumsError = result.error;
        } catch (textSearchError) {
          console.warn('[searchService] textSearch failed for gallery, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if textSearch returned no results or failed
        if ((albumsError || albums.length === 0) && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('photo_albums')
            .select('id, title, description')
            .eq('is_published', true)
            .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('created_at', { ascending: false });
          
          if (!fallbackResult.error && fallbackResult.data) {
            albums = fallbackResult.data;
          }
        }

        if (albums && albums.length > 0) {
          albums.forEach((album: any) => {
            results.push({
              id: album.id,
              type: 'gallery',
              title: album.title || 'Untitled Album',
              excerpt: album.description || '',
              link: `/gallery/${album.id}`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching gallery:', error);
      }
    }

    // Search testimonials
    if (contentType === 'all' || contentType === 'testimonial') {
      try {
        // Try full-text search first
        let testimonials: any[] = [];
        let testimonialsError: any = null;
        
        try {
          const result = await safeFrom('testimonials')
            .select('id, name, content, search_vector')
            .eq('approved', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('id', { ascending: false });
          
          testimonials = result.data || [];
          testimonialsError = result.error;
        } catch (textSearchError) {
          console.warn('[searchService] textSearch failed for testimonials, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if textSearch returned no results or failed
        if ((testimonialsError || testimonials.length === 0) && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('testimonials')
            .select('id, name, content')
            .eq('approved', true)
            .or(`name.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('id', { ascending: false });
          
          if (!fallbackResult.error && fallbackResult.data) {
            testimonials = fallbackResult.data;
          }
        }

        if (testimonials && testimonials.length > 0) {
          testimonials.forEach((testimonial: any) => {
            results.push({
              id: testimonial.id,
              type: 'testimonial',
              title: `Testimonial from ${testimonial.name || 'Anonymous'}`,
              excerpt: testimonial.content || '',
              link: `/#testimonials`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching testimonials:', error);
      }
    }

    // Search daily facts
    if (contentType === 'all' || contentType === 'fact') {
      try {
        // Try full-text search first
        let facts: any[] = [];
        let factsError: any = null;
        
        try {
          const result = await safeFrom('daily_facts')
            .select('id, title, fact_text, search_vector')
            .eq('active', true)
            .not('search_vector', 'is', null)
            .textSearch('search_vector', searchTerm, {
              type: 'websearch',
              config: 'english'
            })
            .limit(limit)
            .order('created_at', { ascending: false });
          
          facts = result.data || [];
          factsError = result.error;
        } catch (textSearchError) {
          console.warn('[searchService] textSearch failed for facts, using ILIKE fallback:', textSearchError);
        }

        // Fallback to ILIKE if textSearch returned no results or failed
        if ((factsError || facts.length === 0) && searchTerm.length > 0) {
          const fallbackResult = await safeFrom('daily_facts')
            .select('id, title, fact_text')
            .eq('active', true)
            .or(`title.ilike.%${searchTerm}%,fact_text.ilike.%${searchTerm}%,source.ilike.%${searchTerm}%`)
            .limit(limit)
            .order('created_at', { ascending: false });
          
          if (!fallbackResult.error && fallbackResult.data) {
            facts = fallbackResult.data;
          }
        }

        if (facts && facts.length > 0) {
          facts.forEach((fact: any) => {
            results.push({
              id: fact.id,
              type: 'fact',
              title: fact.title || 'Daily Fact',
              excerpt: fact.fact_text || '',
              link: `/#facts`,
            });
          });
        }
      } catch (error) {
        console.warn('[searchService] Error searching facts:', error);
      }
    }

    // Apply pagination
    const paginatedResults = results.slice(offset, offset + limit);

    return successResponse(paginatedResults);
  } catch (error) {
    console.error('[searchService] Error in searchAll:', error);
    return errorResponse(handleServiceError(error, 'searchService.searchAll', { query, options }));
  }
};

/**
 * Search a specific content type
 */
export const searchByType = async (
  query: string,
  contentType: SearchContentType,
  options: Omit<SearchOptions, 'contentType'> = {}
): Promise<ServiceResponse<SearchResult[]>> => {
  return searchAll(query, { ...options, contentType });
};

/**
 * Track search query for analytics
 */
const trackSearchQuery = async (
  query: string,
  contentType: string | undefined,
  resultCount: number,
  hasResults: boolean,
  searchTimeMs: number,
  filters?: Record<string, any>
): Promise<void> => {
  if (!isSupabaseAvailable()) return;

  try {
    const { data: currentUser } = await safeAuth.getUser();
    const userId = currentUser?.user?.id || null;
    
    // Generate session ID for anonymous users
    let sessionId = sessionStorage.getItem('search_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('search_session_id', sessionId);
    }

    // Normalize query
    const normalizedQuery = query.toLowerCase().trim().replace(/\s+/g, ' ');

    await safeFrom('search_queries')
      .insert({
        query: query.trim(),
        query_normalized: normalizedQuery,
        content_type: contentType || null,
        filters: filters || null,
        user_id: userId,
        session_id: sessionId,
        result_count: resultCount,
        has_results: hasResults,
        search_time_ms: searchTimeMs,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      });
  } catch (error) {
    // Silently fail analytics tracking
    console.warn('[searchService] Failed to track search query:', error);
  }
};

/**
 * Track search result click
 */
export const trackSearchClick = async (
  searchQueryId: number,
  resultType: string,
  resultId: number,
  resultTitle: string,
  resultLink: string,
  resultPosition: number
): Promise<void> => {
  if (!isSupabaseAvailable()) return;

  try {
    const { data: currentUser } = await safeAuth.getUser();
    const userId = currentUser?.user?.id || null;
    
    let sessionId = sessionStorage.getItem('search_session_id');

    await safeFrom('search_results')
      .insert({
        search_query_id: searchQueryId,
        result_type: resultType,
        result_id: resultId,
        result_title: resultTitle,
        result_link: resultLink,
        result_position: resultPosition,
        was_clicked: true,
        clicked_at: new Date().toISOString(),
        user_id: userId,
        session_id: sessionId,
      });
  } catch (error) {
    console.warn('[searchService] Failed to track search click:', error);
  }
};

/**
 * Get search suggestions (auto-complete)
 */
export const getSearchSuggestions = async (
  query: string,
  limit: number = 10
): Promise<ServiceResponse<SearchSuggestion[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  if (!query || query.trim().length === 0) {
    return successResponse([]);
  }

  try {
    const searchTerm = query.trim().toLowerCase();

    // Get popular suggestions that match
    const { data: suggestions, error } = await safeFrom('search_suggestions')
      .select('*')
      .eq('is_active', true)
      .ilike('suggestion', `%${searchTerm}%`)
      .order('priority', { ascending: false })
      .order('usage_count', { ascending: false })
      .limit(limit);

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch suggestions: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse((suggestions || []) as SearchSuggestion[]);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch search suggestions');
  }
};

/**
 * Get popular searches
 */
export const getPopularSearches = async (
  limit: number = 10,
  days: number = 30
): Promise<ServiceResponse<SearchSuggestion[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const dateThreshold = new Date();
    dateThreshold.setDate(dateThreshold.getDate() - days);

    const { data: suggestions, error } = await safeFrom('search_suggestions')
      .select('*')
      .eq('is_active', true)
      .eq('suggestion_type', 'popular')
      .gte('last_used_at', dateThreshold.toISOString())
      .order('usage_count', { ascending: false })
      .limit(limit);

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch popular searches: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse((suggestions || []) as SearchSuggestion[]);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch popular searches');
  }
};

/**
 * Save search query
 */
export const saveSearch = async (
  name: string,
  query: string,
  contentType?: string,
  filters?: Record<string, any>,
  enableAlerts: boolean = false,
  alertFrequency: 'daily' | 'weekly' | 'immediate' = 'daily'
): Promise<ServiceResponse<SavedSearch>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data: currentUser } = await safeAuth.getUser();
    if (!currentUser?.user?.id) {
      return errorResponse({
        code: 'UNAUTHORIZED',
        message: 'You must be logged in to save searches',
        recoverable: true
      });
    }

    const { data, error } = await safeFrom('saved_searches')
      .insert({
        user_id: currentUser.user.id,
        name,
        query: query.trim(),
        content_type: contentType || null,
        filters: filters || null,
        enable_alerts: enableAlerts,
        alert_frequency: alertFrequency,
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to save search: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as SavedSearch);
  } catch (error) {
    return handleServiceError(error, 'Failed to save search');
  }
};

/**
 * Get user's saved searches
 */
export const getSavedSearches = async (): Promise<ServiceResponse<SavedSearch[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data: currentUser } = await safeAuth.getUser();
    if (!currentUser?.user?.id) {
      return errorResponse({
        code: 'UNAUTHORIZED',
        message: 'You must be logged in to view saved searches',
        recoverable: true
      });
    }

    const { data, error } = await safeFrom('saved_searches')
      .select('*')
      .eq('user_id', currentUser.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch saved searches: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse((data || []) as SavedSearch[]);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch saved searches');
  }
};

/**
 * Get search analytics (admin only)
 */
export const getSearchAnalytics = async (
  dateFrom?: string,
  dateTo?: string,
  limit: number = 100
): Promise<ServiceResponse<SearchAnalytics[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    let query = safeFrom('search_analytics')
      .select('*')
      .order('date', { ascending: false })
      .order('hour', { ascending: false })
      .limit(limit);

    if (dateFrom) {
      query = query.gte('date', dateFrom);
    }
    if (dateTo) {
      query = query.lte('date', dateTo);
    }

    const { data, error } = await query;

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch search analytics: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse((data || []) as SearchAnalytics[]);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch search analytics');
  }
};

// Enhanced searchAll with analytics tracking
const searchAllWithTracking = async (
  query: string,
  options: SearchOptions = {}
): Promise<ServiceResponse<SearchResult[]>> => {
  const startTime = performance.now();
  
  // Perform search
  const result = await searchAll(query, options);
  
  const searchTimeMs = Math.round(performance.now() - startTime);

  // Track search if enabled
  if (options.trackAnalytics !== false && result.success && result.data) {
    await trackSearchQuery(
      query,
      options.contentType,
      result.data.length,
      result.data.length > 0,
      searchTimeMs,
      {
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        category: options.category,
        tags: options.tags,
        author: options.author,
        status: options.status,
      }
    );
  }

  return result;
};

export const searchService = {
  searchAll: searchAllWithTracking,
  searchByType,
  trackSearchClick,
  getSearchSuggestions,
  getPopularSearches,
  saveSearch,
  getSavedSearches,
  getSearchAnalytics,
};

